import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatArticleCategories } from '../utils/articleCategories';
import { parseApiDate } from '../utils/parseApiDate';
import './RecentHeadlinesPreview.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TICKER_FETCH_SIZE = 14;

function formatRelativeTime(dateString) {
  const date = parseApiDate(dateString);
  if (!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return 'Just now';
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function useDeferredEnabled(eager = false) {
  const containerRef = useRef(null);
  const [enabled, setEnabled] = useState(eager);

  useEffect(() => {
    if (eager) {
      return undefined;
    }
    const node = containerRef.current;
    if (!node) return undefined;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            setEnabled(true);
            observer.disconnect();
          }
        },
        { rootMargin: '120px' },
      );
      observer.observe(node);
      return () => observer.disconnect();
    }

    setEnabled(true);
    return undefined;
  }, [eager]);

  return { containerRef, enabled };
}

function TickerItem({ article }) {
  const cats = formatArticleCategories(article);
  return (
    <span className="headlines-ticker-item">
      <Link to={`/article/${article.id}`} className="headlines-ticker-link">
        <span className="headlines-ticker-title">{article.title}</span>
        {(article.source || article.publishedAt || cats) && (
          <span className="headlines-ticker-meta">
            {article.source && <span>{article.source}</span>}
            {cats && <span>{cats}</span>}
            {article.publishedAt && (
              <span>{formatRelativeTime(article.publishedAt)}</span>
            )}
          </span>
        )}
      </Link>
    </span>
  );
}

const RecentHeadlinesPreview = ({ placement = 'inline' }) => {
  const eager = placement === 'top';
  const { containerRef, enabled } = useDeferredEnabled(eager);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetch(
      `${API_BASE_URL}/feed?page=0&size=${TICKER_FETCH_SIZE}&sortBy=publishedAt&sortDir=desc&lite=true&skipTotal=true`,
      { headers: { Accept: 'application/json' } },
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          setArticles(data.content || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const loopItems = useMemo(
    () => (articles.length > 0 ? [...articles, ...articles] : []),
    [articles],
  );

  const tickerStyle = useMemo(() => {
    if (articles.length === 0) return undefined;
    const seconds = Math.max(28, articles.length * 5);
    return { '--headlines-ticker-duration': `${seconds}s` };
  }, [articles.length]);

  return (
    <div
      ref={containerRef}
      className={`headlines-ticker${placement === 'top' ? ' headlines-ticker--top' : ''}`}
      aria-busy={loading}
      aria-live="polite"
      style={tickerStyle}
    >
      <div className="headlines-ticker-inner container">
        <div className="headlines-ticker-label" aria-hidden="true">
          <span className="headlines-ticker-live-dot" />
          Latest
        </div>

        <div className="headlines-ticker-viewport">
          {loading && (
            <p className="headlines-ticker-status">Loading headlines…</p>
          )}
          {!loading && error && (
            <p className="headlines-ticker-status">
              Headlines unavailable —{' '}
              <Link to="/feed">open the feed</Link>
            </p>
          )}
          {!loading && !error && articles.length === 0 && (
            <p className="headlines-ticker-status">
              No headlines yet —{' '}
              <Link to="/feed">browse the feed</Link>
            </p>
          )}
          {!loading && !error && articles.length > 0 && (
            <div className="headlines-ticker-track">
              <div className="headlines-ticker-scroll">
                {loopItems.map((article, index) => (
                  <TickerItem
                    key={`${article.id}-${index}`}
                    article={article}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <Link to="/desk" className="headlines-ticker-all">
          All headlines →
        </Link>
      </div>
    </div>
  );
};

export default RecentHeadlinesPreview;
